/**
 * RebalanceProposal — TASK-868. The surface that can act on a breach the product already prices.
 *
 * Five screens detect Hubertus Schneider's mandate breach and size it to the franc; none could
 * change an allocation. The one chip called `Rebalance` opens the same-slot instrument swap,
 * which renders the live breach inside itself, unchanged, and says so.
 *
 * Two things this panel refuses to do, because they are the defects the epic is arranged around:
 *
 *  - **It never names a position the client does not hold.** Every row comes from the server,
 *    which picks holdings with the same classification the drift engine used to raise the
 *    breach. Asked to trim CHF 384K, the assistant invented a ticket naming four positions
 *    Schneider does not own (TASK-870); a row here cannot be invented.
 *  - **It never reports success it did not achieve.** `opens_new_breach` is computed server-side
 *    by re-running the real drift calculation over the trimmed book. On this client every trim
 *    of Foreign (Dev. Markets) into cash pushes Cash & Money Market through its own band — the
 *    assistant asked to simulate exactly that stamped "✅ the drift breach would be resolved"
 *    (TASK-872). Here the banner says so, and Save stays available because an advisor may still
 *    want the record — it is a verdict, not a gate.
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAccount } from '../../contexts/AccountContext';
import {
  portfolioApiService,
  type RebalanceProposal as Proposal,
} from '../../services/api/portfolioApiService';

export interface RebalanceProposalProps {
  clientId: string;
  slot: string;
  portfolioId?: string;
  onClose: () => void;
}

const chf = (value: number) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    maximumFractionDigits: 0,
  }).format(value);

const pct = (value: number) => `${value.toFixed(2)}%`;

/** The message a failed build actually carries — FastAPI puts the useful part in `detail`. */
function buildError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message);
  }
  return 'The proposal could not be built.';
}

const RebalanceProposal: React.FC<RebalanceProposalProps> = ({
  clientId,
  slot,
  portfolioId,
  onClose,
}) => {
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // Attribution for the persisted record — see `savePortfolioProposal`.
  const { user } = useAccount();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProposal(null);
    setSavedAt(null);

    portfolioApiService
      .buildRebalanceProposal(clientId, slot, portfolioId)
      .then((built) => {
        if (!cancelled) setProposal(built);
      })
      .catch((err) => {
        if (!cancelled) setError(buildError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, slot, portfolioId]);

  const handleSave = async () => {
    if (!proposal) return;
    setSaving(true);
    try {
      await portfolioApiService.savePortfolioProposal(clientId, proposal, user?.id);
      setSavedAt(new Date().toLocaleString('de-CH'));
      toast.success('Proposal saved');
    } catch (err) {
      toast.error(buildError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex items-center gap-3 text-sm text-gray-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        Sizing a proposal for {slot}…
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">No proposal for {slot}</h4>
            <p className="text-sm text-gray-600 mt-1">{error}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Only the slots the trim actually moved. A 12-row before/after table where 10 rows are
  // identical buries the two that changed.
  const movedSlots = Object.keys(proposal.drift_after.by_slot).filter((key) => {
    const before = proposal.drift_before.by_slot[key];
    const after = proposal.drift_after.by_slot[key];
    return before && after && Math.abs(after.current - before.current) >= 0.01;
  });

  // Success is both halves, and neither implies the other: a proposal can leave the slot
  // outside its band, or fix it and break something else, or do nothing at all.
  const succeeded = proposal.resolves_target_slot && !proposal.opens_new_breach;

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="rebalance-proposal">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Reduce {proposal.slot} by {chf(proposal.trim_chf)}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Across {proposal.rows.length}{' '}
            {proposal.rows.length === 1 ? 'holding' : 'holdings'} in this slot, pro-rata, sized in{' '}
            {proposal.sizing_unit}.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-gray-500 hover:text-gray-700 flex-shrink-0"
        >
          Close
        </button>
      </div>

      {/* The verdict. Computed, not narrated — see the file header.
          Green requires BOTH halves: the slot is fixed AND nothing else broke. Review caught
          the first cut keying only on `opens_new_breach`, which rendered "brings it inside its
          band and opens no new breach" over a proposal that had moved nothing at all. Reporting
          a success it did not achieve is the one thing this panel exists not to do. */}
      <div
        className={`mt-4 rounded-md p-4 flex items-start gap-3 ${
          succeeded
            ? 'bg-emerald-50 border border-emerald-200'
            : 'bg-amber-50 border border-amber-200'
        }`}
        data-testid="rebalance-verdict"
      >
        {succeeded ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        )}
        <div className="text-sm">
          {succeeded ? (
            <p className="font-semibold text-emerald-900">
              This brings {proposal.slot} inside its band and opens no new breach. Breaches
              would go from {proposal.breaches_before.length} to{' '}
              {proposal.breaches_after.length}.
            </p>
          ) : (
            <>
              <p className="font-semibold text-amber-900">
                {proposal.resolves_target_slot
                  ? `This resolves ${proposal.slot} and opens a new breach in `
                    + `${proposal.new_breaches.join(', ')}.`
                  : proposal.new_breaches.length > 0
                    ? `This does not bring ${proposal.slot} inside its band, and opens a new `
                      + `breach in ${proposal.new_breaches.join(', ')}.`
                    : `This does not bring ${proposal.slot} inside its band.`}
              </p>
              <p className="text-amber-800 mt-1">
                The proceeds are credited to cash, which moves the Liquidity weight. Breaches
                would go from {proposal.breaches_before.length} to{' '}
                {proposal.breaches_after.length}.
              </p>
            </>
          )}
        </div>
      </div>

      {proposal.shortfall_chf > 0 && (
        <p className="mt-3 text-sm text-amber-700">
          {chf(proposal.shortfall_chf)} of the required reduction could not be sized — this slot
          does not hold enough that can be trimmed.
        </p>
      )}

      {/* Rows — every one a position this client holds. */}
      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Holding
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Current
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Reduce by
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                After
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {proposal.rows.map((row) => (
              <tr key={row.position_id}>
                <td className="px-4 py-2 text-sm text-gray-900">
                  {row.security_name}
                  {row.isin && <span className="text-gray-400 ml-2 text-xs">{row.isin}</span>}
                </td>
                <td className="px-4 py-2 text-sm text-gray-600 text-right">
                  {chf(row.current_value_chf)}
                </td>
                <td className="px-4 py-2 text-sm text-red-700 text-right font-medium">
                  −{chf(row.trim_chf)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 text-right">
                  {chf(row.post_value_chf)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!proposal.share_sizing_available && (
        <p className="mt-3 text-xs text-gray-500">
          Sized in CHF, not shares: this custodian feed carries no usable unit price for these
          holdings.
        </p>
      )}

      {/* Before/after across the mandate — only the slots that moved. */}
      <h4 className="mt-6 text-sm font-semibold text-gray-900">Effect on the mandate</h4>
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Slot
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Target
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Before
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                After
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {movedSlots.map((key) => {
              const before = proposal.drift_before.by_slot[key];
              const after = proposal.drift_after.by_slot[key];
              return (
                <tr key={key}>
                  <td className="px-4 py-2 text-sm text-gray-900">{key}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 text-right">
                    {pct(after.target)}
                  </td>
                  <td
                    className={`px-4 py-2 text-sm text-right ${
                      before.breach ? 'text-red-700 font-medium' : 'text-gray-600'
                    }`}
                  >
                    {pct(before.current)}
                    {before.breach && ' ⚠'}
                  </td>
                  <td
                    className={`px-4 py-2 text-sm text-right ${
                      after.breach ? 'text-red-700 font-medium' : 'text-emerald-700'
                    }`}
                  >
                    {pct(after.current)}
                    {after.breach && ' ⚠'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <p className="text-xs text-gray-500">
          Assessed against this client&apos;s own ±{proposal.tolerance_pct ?? '—'}pp tolerance at
          sub-asset-class level
          {proposal.calculation_version ? ` · calculation v${proposal.calculation_version}` : ''}
          {savedAt ? ` · saved ${savedAt}` : ''}
        </p>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-slate-800 rounded hover:bg-slate-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {savedAt ? 'Save again' : 'Save proposal'}
        </button>
      </div>
    </div>
  );
};

export default RebalanceProposal;
